(() => {
  'use strict';

  /* ============================================================
   * 1. CONFIGURATION
   * ============================================================
   * Keep values that may change in one place.
   */
  const CONFIG = {
    endpoint: 'https://request-draft.cleanflow.net/draft-order',

    messages: {
      processing: 'Processing…',
      turnstileRequired: 'Please complete the security verification.',
      turnstileFailed: 'Security verification failed or expired. Please try again.',
      vendorNotAllowed: 'Quote requests are not available for this product.',
      genericError:
        'Sorry, we could not create your request. Please try again or contact support.',
      networkError: 'Network error. Please try again in a moment.',
      success:
        'Thank you for your quote request. Please check your inbox within the next few minutes to view your quotation and pricing. If you don’t see it in your main inbox, please check your junk or spam folder.',
    },
  };

  /*
   * These Worker error codes all show the same security message.
   */
  const TURNSTILE_ERROR_CODES = new Set([
    'TURNSTILE_FAILED',
    'TURNSTILE_ACTION_MISMATCH',
    'TURNSTILE_HOSTNAME_MISMATCH',
  ]);

  /* ============================================================
   * 2. GET REQUIRED HTML ELEMENTS
   * ============================================================
   */
  const form = document.getElementById('draft-order-form');
  const messageElement = document.getElementById('draft-order-message');
  const submitButton = document.getElementById('draft-order-submit');

  const productImage = document.getElementById('quote-product-image');
  const variantTitleElement = document.getElementById('quote-variant-title');
  const variantSkuElement = document.getElementById('quote-variant-sku');

  const variantInput = form?.querySelector('[name="variantId"]');
  const quantityInput = form?.querySelector('[name="quantity"]');

  /*
   * Stop the script when an important form element is missing.
   *
   * The product image is not included here because it is optional.
   * The form can still work without it.
   */
  const requiredElements = [
    form,
    messageElement,
    submitButton,
    variantInput,
    variantTitleElement,
    variantSkuElement,
    quantityInput,
  ];

  if (requiredElements.some((element) => !element)) {
    console.warn(
      'Draft order form was not initialized because one or more required elements are missing.'
    );

    return;
  }

  /* ============================================================
   * 3. FORM STATE
   * ============================================================
   */
  const originalButtonText = submitButton.textContent.trim();

  /*
   * Track the currently selected Shopify variant.
   *
   * This prevents the form from submitting the first/default variant
   * after the customer selects another variant.
   */
  let currentVariantId = variantInput.value;
  let currentVariant = null;

  /* ============================================================
   * 4. GENERAL HELPER FUNCTIONS
   * ============================================================
   */

  /**
   * Read a FormData value and safely return it as trimmed text.
   *
   * @param {FormData} formData
   * @param {string} fieldName
   * @returns {string}
   */
  function getFormValue(formData, fieldName) {
    return String(formData.get(fieldName) || '').trim();
  }

  /**
   * Safely parse a number as an integer.
   *
   * @param {unknown} value
   * @param {number} fallback
   * @returns {number}
   */
  function getInteger(value, fallback = 1) {
    const parsedValue = Number.parseInt(String(value), 10);

    return Number.isInteger(parsedValue) ? parsedValue : fallback;
  }

  /**
   * Attempt to read the server response as JSON.
   *
   * Returns an empty object when the response is not valid JSON.
   *
   * @param {Response} response
   * @returns {Promise<object>}
   */
  async function parseJsonResponse(response) {
    try {
      return await response.json();
    } catch (error) {
      console.error('The server returned an invalid JSON response:', error);

      return {};
    }
  }

  /* ============================================================
   * 5. VARIANT AND PRODUCT SUMMARY FUNCTIONS
   * ============================================================
   */

  /**
   * Get the best available image from a Shopify variant.
   *
   * @param {object} variant
   * @returns {string}
   */
  function getVariantImage(variant) {
    return (
      variant?.featured_media?.preview_image?.src ||
      variant?.featured_image?.src ||
      ''
    );
  }

  /**
   * Get the best available title from a Shopify variant.
   *
   * @param {object} variant
   * @returns {string}
   */
  function getVariantTitle(variant) {
    return (
      variant?.public_title ||
      variant?.title ||
      variant?.option1 ||
      ''
    );
  }

  /**
   * Update the minimum, maximum and increment allowed for quantity.
   *
   * Shopify may provide rules such as:
   * - Minimum quantity: 2
   * - Maximum quantity: 20
   * - Increment: 2
   *
   * @param {object} variant
   */
  function updateQuantityRules(variant) {
    const quantityRule = variant.quantity_rule || {};

    const minimum = getInteger(quantityRule.min, 1);
    const increment = getInteger(quantityRule.increment, 1);

    const hasMaximum =
      quantityRule.max !== null &&
      quantityRule.max !== undefined &&
      quantityRule.max !== '';

    const maximum = hasMaximum
      ? getInteger(quantityRule.max, minimum)
      : null;

    quantityInput.min = String(minimum);
    quantityInput.step = String(increment);

    if (maximum !== null) {
      quantityInput.max = String(maximum);
    } else {
      quantityInput.removeAttribute('max');
    }

    let currentQuantity = getInteger(quantityInput.value, minimum);

    /*
     * Make sure the current quantity is not below the minimum.
     */
    if (currentQuantity < minimum) {
      currentQuantity = minimum;
    }

    /*
     * Make sure the current quantity is not above the maximum.
     */
    if (maximum !== null && currentQuantity > maximum) {
      currentQuantity = maximum;
    }

    quantityInput.value = String(currentQuantity);
  }

  /**
   * Update the product information shown inside the quote form.
   *
   * @param {object} variant
   */
  function updateProductSummary(variant) {
    if (!variant?.id) {
      return;
    }

    /*
     * Save the currently selected variant so the correct variant
     * is submitted and restored after resetting the form.
     */
    currentVariant = variant;
    currentVariantId = String(variant.id);
    variantInput.value = currentVariantId;

    /*
     * Update the variant image when an image is available.
     */
    const imageSource = getVariantImage(variant);

    if (imageSource && productImage) {
      productImage.src = imageSource;

      productImage.alt =
        variant.featured_media?.alt ||
        variant.featured_image?.alt ||
        variant.name ||
        getVariantTitle(variant) ||
        'Selected product';
    }

    /*
     * Update or hide the variant title.
     *
     * "Default Title" normally means the product has no real variants.
     */
    const displayVariantTitle = getVariantTitle(variant);
    const hasRealVariantTitle =
      displayVariantTitle &&
      displayVariantTitle !== 'Default Title';

    variantTitleElement.textContent = hasRealVariantTitle
      ? displayVariantTitle
      : '';

    variantTitleElement.hidden = !hasRealVariantTitle;

    /*
     * Update or hide the SKU.
     */
    const hasSku = Boolean(variant.sku);

    variantSkuElement.textContent = hasSku
      ? `SKU: ${variant.sku}`
      : '';

    variantSkuElement.hidden = !hasSku;

    /*
     * Apply Shopify's quantity rules for this variant.
     */
    updateQuantityRules(variant);
  }

  /*
   * Listen for the custom event fired when the customer selects
   * a different product variant.
   */
  document.addEventListener('variant:changed', (event) => {
    const selectedVariant = event.detail?.variant;

    if (selectedVariant?.id) {
      updateProductSummary(selectedVariant);
    }
  });

  /* ============================================================
   * 6. MESSAGE AND BUTTON FUNCTIONS
   * ============================================================
   */

  /**
   * Show a success or error message.
   *
   * @param {string} text
   * @param {'success'|'error'} type
   */
  function showMessage(text, type = 'error') {
    const isSuccess = type === 'success';

    messageElement.textContent = text;
    messageElement.hidden = false;

    messageElement.classList.remove(
      'alert-success',
      'alert-danger'
    );

    messageElement.classList.add(
      isSuccess ? 'alert-success' : 'alert-danger'
    );
  }

  /**
   * Clear and hide the current form message.
   */
  function hideMessage() {
    messageElement.textContent = '';
    messageElement.hidden = true;

    messageElement.classList.remove(
      'alert-success',
      'alert-danger'
    );
  }

  /**
   * Change the submit button while the request is processing.
   *
   * @param {boolean} isSubmitting
   */
  function setSubmitting(isSubmitting) {
    submitButton.disabled = isSubmitting;

    submitButton.setAttribute(
      'aria-busy',
      isSubmitting ? 'true' : 'false'
    );

    submitButton.textContent = isSubmitting
      ? CONFIG.messages.processing
      : originalButtonText;
  }

  /**
   * Reset the Cloudflare Turnstile widget.
   */
  function resetTurnstile() {
    if (
      window.turnstile &&
      typeof window.turnstile.reset === 'function'
    ) {
      window.turnstile.reset();
    }
  }

  /**
   * Convert a Worker error code into a customer-friendly message.
   *
   * @param {string} errorCode
   * @returns {string}
   */
  function getServerErrorMessage(errorCode) {
    if (TURNSTILE_ERROR_CODES.has(errorCode)) {
      return CONFIG.messages.turnstileFailed;
    }

    if (errorCode === 'VENDOR_NOT_ALLOWED') {
      return CONFIG.messages.vendorNotAllowed;
    }

    return CONFIG.messages.genericError;
  }

  /* ============================================================
   * 7. FORM SUBMISSION
   * ============================================================
   */
  form.addEventListener('submit', async (event) => {
    /*
     * Prevent the browser from submitting the form normally.
     * The form will instead be sent through fetch().
     */
    event.preventDefault();

    hideMessage();

    /*
     * This is an additional validation check.
     *
     * Normally, the browser validates required fields before firing
     * the submit event, but this also protects against submissions
     * triggered through JavaScript.
     */
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    /*
     * Ensure the hidden variant field contains the variant that
     * the customer currently selected.
     */
    variantInput.value = currentVariantId;

    const formData = new FormData(form);
    const turnstileToken = getFormValue(
      formData,
      'cf-turnstile-response'
    );

    /*
     * Do not submit without Cloudflare Turnstile verification.
     */
    if (!turnstileToken) {
      showMessage(CONFIG.messages.turnstileRequired);
      return;
    }

    /*
     * Use the quantity field's minimum as the fallback quantity.
     */
    const fallbackQuantity = getInteger(quantityInput.min, 1);
    const quantity = getInteger(
      formData.get('quantity'),
      fallbackQuantity
    );

    /*
     * Build the JSON data sent to the Cloudflare Worker.
     */
    const payload = {
      variantId: getFormValue(formData, 'variantId'),
      quantity,
      email: getFormValue(formData, 'email'),
      countryCode: getFormValue(formData, 'countryCode'),
      firstName: getFormValue(formData, 'firstName'),
      lastName: getFormValue(formData, 'lastName'),
      company: getFormValue(formData, 'company'),
      address1: getFormValue(formData, 'address1'),
      address2: getFormValue(formData, 'address2'),
      city: getFormValue(formData, 'city'),
      postalCode: getFormValue(formData, 'postalCode'),
      phone: getFormValue(formData, 'phone'),
      customerMessage: getFormValue(formData, 'customerMessage'),
      turnstileToken,
    };

    setSubmitting(true);

    try {
      /*
       * Send the quote request to the Cloudflare Worker.
       */
      const response = await fetch(CONFIG.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await parseJsonResponse(response);

      /*
       * Treat the request as failed when:
       * 1. The HTTP response is unsuccessful, or
       * 2. The Worker does not return success: true.
       */
      if (!response.ok || !result.success) {
        console.error('Error returned by the Worker:', {
          status: response.status,
          result,
        });

        const errorMessage = getServerErrorMessage(result.code);

        showMessage(errorMessage);
        return;
      }

      /*
       * Clear the customer's form fields after a successful request.
       */
      form.reset();

      /*
       * form.reset() restores the original product variant.
       * Set it back to the variant currently selected by the customer.
       */
      variantInput.value = currentVariantId;

      /*
       * Reapply the current variant's image, SKU and quantity rules.
       */
      if (currentVariant) {
        updateProductSummary(currentVariant);
      }

      showMessage(CONFIG.messages.success, 'success');
    } catch (error) {
      /*
       * This normally catches:
       * - Internet connection problems
       * - DNS problems
       * - Blocked requests
       * - Unexpected JavaScript errors
       */
      console.error('Network or JavaScript error:', error);

      showMessage(CONFIG.messages.networkError);
    } finally {
      /*
       * Always reset the security widget and restore the button,
       * whether the request succeeded or failed.
       */
      resetTurnstile();
      setSubmitting(false);
    }
  });
})();